"use client";

import { type AnyRpcModule, createRpcClient } from "@packages/confect/rpc";
import { sharedConvexClientLayer } from "@packages/ui/components/convex-client-provider";
import {
	createContext,
	createElement,
	type ReactNode,
	useContext,
} from "react";

type ClientProviderProps<TClient> = {
	readonly client: TClient;
	readonly children?: ReactNode;
};

type ClientContextApi<TClient> = {
	readonly RpcClientProvider: (
		props: ClientProviderProps<TClient>,
	) => ReactNode;
	readonly useRpcClient: () => TClient;
};

type ModuleApi<TModule extends AnyRpcModule> = Parameters<
	typeof createRpcClient<TModule>
>[0];

export const createRpcClientContext = <TClient>(
	createDefaultClient: () => TClient,
): ClientContextApi<TClient> => {
	const ClientContext = createContext<TClient | null>(null);
	let defaultClient: TClient | null = null;

	const getDefaultClient = () => {
		if (defaultClient === null) {
			defaultClient = createDefaultClient();
		}

		return defaultClient;
	};

	const RpcClientProvider = ({
		client,
		children,
	}: ClientProviderProps<TClient>) =>
		createElement(ClientContext.Provider, { value: client }, children);

	const useRpcClient = () => {
		const client = useContext(ClientContext);
		return client ?? getDefaultClient();
	};

	return {
		RpcClientProvider,
		useRpcClient,
	};
};

export const createRpcModuleClientContext = <TModule extends AnyRpcModule>(
	moduleApi: ModuleApi<TModule>,
) =>
	createRpcClientContext(() =>
		createRpcClient<TModule>(moduleApi, {
			layer: sharedConvexClientLayer,
		}),
	);
